import { useCallback } from "react";

import { type FormField, type FormValues, prepareFormValues, useDynamicForm } from "@prequel/import-react";

import { type DestinationOptions, type UpdateDestinationOptions } from "@/api/import/destinations";
import { fetchDestinationForm, type FormMode } from "@/api/import/forms";

export const DESTINATION_ROOT_KEY = "destination";

export type UseDestinationFormResult = {
  draft: FormValues;
  fields: FormField[];
  setFieldValue: (field: FormField, value: unknown) => void;
  getDestination: () => DestinationOptions;
  getDestinationUpdate: () => UpdateDestinationOptions;
  isLoading: boolean;
  isRefetching: boolean;
  error: unknown;
};

type UseDestinationFormOptions = {
  initialValues?: FormValues;
  mode?: FormMode;
};

/**
 * Drives the destination form from the fields the API generates for the current draft.
 * Changing the destination type re-requests the form with the selected branch.
 *
 * In edit mode the API marks the fields a PATCH cannot change as read-only, so they
 * render as values rather than inputs and stay out of the payload.
 */
export function useDestinationForm({
  initialValues,
  mode = "create",
}: UseDestinationFormOptions = {}): UseDestinationFormResult {
  const fetchForm = useCallback((values: FormValues) => fetchDestinationForm(values, mode), [mode]);
  const { values, fields, setFieldValue, isLoading, isRefetching, error } = useDynamicForm({
    fetchForm,
    rootKey: DESTINATION_ROOT_KEY,
    initialValues,
  });
  const getDestination = useCallback(
    () => prepareFormValues(values, fields, DESTINATION_ROOT_KEY) as DestinationOptions,
    [fields, values],
  );
  const getDestinationUpdate = useCallback(
    () => prepareFormValues(values, fields, DESTINATION_ROOT_KEY) as UpdateDestinationOptions,
    [fields, values],
  );

  return {
    draft: values,
    fields,
    setFieldValue,
    getDestination,
    getDestinationUpdate,
    isLoading,
    isRefetching,
    error,
  };
}
